/*
 * <cart-drawer-key-options> — key selection inside the cart drawer.
 * ------------------------------------------------------------------------
 * A dropdown/collapsible re-implementation of the cart-page key selector
 * (assets/key-options.js `KeyOptions`). It is fully interoperable with the
 * cart page: it reads/writes the SAME cart-note format
 *
 *     Key Option: <selection>
 *     Key Number: <keyNumber>      (only for "Use Existing Key Number")
 *     Key Code: <keyCode>
 *
 * where <selection> ∈ { "New Key", "Use Existing Key Number", "Keyed Alike" }.
 *
 * Self-initializing: the drawer HTML is replaced on every cart refresh, so the
 * element re-runs connectedCallback each time and restores its state from the
 * server-rendered `data-cart-note`. It gates the drawer's "Proceed to checkout"
 * button (#CartDrawer-Checkout) and the express-pay buttons until a valid
 * selection exists — matching the cart page, which gates #checkout.
 */
/**
 * Has the customer picked a key option THEMSELVES, on this page, for the cart as it
 * stands now?
 *
 * `cart.note` cannot answer that. Shopify keeps the note — and the cart token, and
 * the cart attributes — when a cart is emptied, so "New Key chosen for an earlier
 * cart, cart emptied, the same product added back" is byte-for-byte identical on the
 * server to "New Key chosen for this cart". There is nothing to read that tells them
 * apart, and no amount of clearing the note can be relied on: that clear is a network
 * request, and a request can be lost to a navigation.
 *
 * So the radio state is re-derived from the offer rules on every fresh render — a
 * bundle or 2+ locks pre-selects "Keyed Alike — New Key", a single regular lock
 * selects nothing and leaves checkout gated — and only a choice the customer made
 * here, for this cart, overrides them.
 *
 * The flag is deliberately plain local state, never persisted: it costs no request,
 * so it cannot lose a race. It is reset by the two things that end a cart's claim to
 * a choice: a page load (a new document starts false) and the cart going empty.
 *
 * It hangs off `window` so the drawer and the cart-page selector (assets/key-options.js)
 * agree on a single page that renders both.
 *
 * Read and written as a bare window property on purpose. This file and key-options.js
 * are both classic scripts on the same page, so a shared top-level `const` would be a
 * redeclaration in the global lexical scope and throw a SyntaxError that kills both.
 */

// An empty cart has no locks, so it has no key option — whatever was picked for the
// previous contents stops counting here. Local flag only; nothing is written.
document.addEventListener('cart:updated', (event) => {
  const cart = event.detail && event.detail.cart;
  if (cart && cart.item_count === 0) window.__ampKeyOptionPickedThisPageSession = false;
});

class CartDrawerKeyOptions extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    this.box = this.querySelector('.cart-drawer-key__box');
    this.header = this.querySelector('.cart-drawer-key__header');
    this.panel = this.querySelector('.cart-drawer-key__panel');
    this.summaryEl = this.querySelector('.cart-drawer-key__summary');
    this.placeholderEl = this.querySelector('.cart-drawer-key__placeholder');
    this.selectedTitleEl = this.querySelector('.cart-drawer-key__selected-title');
    this.radios = Array.from(this.querySelectorAll('input[name="drawer-key-selection"]'));
    this.cards = Array.from(this.querySelectorAll('.cart-drawer-key__card'));
    this.existingCard = this.querySelector('.cart-drawer-key__card--existing');
    this.fields = this.querySelector('.cart-drawer-key__fields');
    this.keyNumberInput = this.querySelector('#drawer-key-number');
    this.keyCodeInput = this.querySelector('#drawer-key-code');
    this.keyNumberError = this.querySelector('[data-error-for="key-number"]');
    this.keyCodeError = this.querySelector('[data-error-for="key-code"]');

    this.itemsCount = parseInt(this.dataset.itemsCount || '0', 10);
    this.labels = {};
    this.radios.forEach((r) => {
      this.labels[r.value] = r.dataset.title || r.value;
    });
    this.keyNumberLabel = this.dataset.keyNumberLabel || 'Key number';
    this.keyCodeLabel = this.dataset.keyCodeLabel || 'Key code';
    this.disabledText = this.dataset.disabledText || 'Select your Key Options';
    this.preselectEnabled = this.dataset.preselectKeyedAlike === 'true';

    if (!this.header || !this.radios.length) return;

    this.expanded = false;

    // --- events -----------------------------------------------------------
    // This element lives inside <cart-drawer-items>, whose inherited CartItems
    // delegated `change` handler would otherwise treat our radio/input changes
    // as line-item quantity changes (validateQuantity → updateQuantity with an
    // undefined index → bogus cart mutation). Stop our change/input events from
    // bubbling out of the component.
    this.addEventListener('change', (event) => event.stopPropagation());
    this.addEventListener('input', (event) => event.stopPropagation());

    this.header.addEventListener('click', () => this.toggle());

    this.radios.forEach((radio) => {
      radio.addEventListener('change', () => {
        window.__ampKeyOptionPickedThisPageSession = true;
        this.onSelect();
      });
    });

    if (this.keyNumberInput) {
      this.keyNumberInput.addEventListener('input', (e) => {
        e.target.value = e.target.value.replace(/[^a-zA-Z0-9]/g, '').substring(0, 7);
        this.onFieldInput();
      });
    }
    if (this.keyCodeInput) {
      this.keyCodeInput.addEventListener('input', (e) => {
        e.target.value = e.target.value.replace(/\D/g, '').substring(0, 7);
        this.onFieldInput();
      });
    }

    // Restore selection from the cart note, then paint the UI + gate checkout.
    this.restoreFromNote();
    this.preselectKeyedAlike();
    this.render();
    this.updateCheckout();
  }

  // ---- state helpers -----------------------------------------------------
  getSelectedRadio() {
    return this.radios.find((r) => r.checked && !this.isCardHidden(r)) || null;
  }

  getSelectedValue() {
    const r = this.getSelectedRadio();
    return r ? r.value : '';
  }

  isCardHidden(radio) {
    const card = radio.closest('.cart-drawer-key__card');
    return !card || card.hasAttribute('hidden');
  }

  needsKeyFields(value) {
    return value === 'Use Existing Key Number';
  }

  keyNumberValid() {
    const v = this.keyNumberInput ? this.keyNumberInput.value.trim() : '';
    return v.length >= 3 && v.length <= 7;
  }

  keyCodeValid() {
    const v = this.keyCodeInput ? this.keyCodeInput.value.trim() : '';
    return v.length === 7;
  }

  /** Whether the current selection is complete and safe to check out. */
  isValid() {
    const value = this.getSelectedValue();
    if (!value) return false;
    if (this.needsKeyFields(value)) return this.keyNumberValid() && this.keyCodeValid();
    return true;
  }

  /**
   * Pre-select "Keyed Alike — New Key" whenever that card is offered and the
   * customer has not chosen anything yet, and write it to the cart note right
   * away so the choice reaches the order even if the panel is never opened.
   *
   * Runs after restoreFromNote, so an existing choice always wins. When the
   * card is hidden (a single lock in the cart) nothing is selected and checkout
   * stays gated, exactly as before. Controlled by the "Pre-select Keyed Alike
   * — New Key" theme setting; off means no selection is made for the customer.
   */
  preselectKeyedAlike() {
    if (!this.preselectEnabled) return;

    const radio = this.radios.find((r) => r.value === 'Keyed Alike');
    if (!radio || this.isCardHidden(radio)) return;
    if (this.getSelectedValue()) return;

    radio.checked = true;
    this.cards.forEach((card) => {
      const r = card.querySelector('input[type="radio"]');
      card.classList.toggle('is-selected', !!r && r.checked);
    });
    if (this.fields) this.fields.hidden = true;

    // persist() gates checkout until the note lands, which matters most here:
    // the customer never made this choice, so the drawer must not look ready
    // to check out before the cart actually carries it.
    this.persist();
  }

  // ---- interactions ------------------------------------------------------
  toggle() {
    this.expanded = !this.expanded;
    this.render();
  }

  onSelect() {
    const value = this.getSelectedValue();

    // Toggle selected styling on cards + show fields only for "Use Existing".
    this.cards.forEach((card) => {
      const radio = card.querySelector('input[type="radio"]');
      card.classList.toggle('is-selected', !!radio && radio.checked);
    });
    if (this.fields) this.fields.hidden = !this.needsKeyFields(value);

    if (this.needsKeyFields(value)) {
      // Focus the first empty key field to guide the customer.
      if (this.keyNumberInput && !this.keyNumberInput.value) this.keyNumberInput.focus();
      this.validateFields();
    } else {
      this.clearFieldErrors();
    }

    this.persist();
    this.render();
    this.updateCheckout();
  }

  onFieldInput() {
    this.validateFields();
    this.persist();
    this.render();
    this.updateCheckout();
  }

  validateFields() {
    const numFilled = this.keyNumberInput && this.keyNumberInput.value.length > 0;
    const codeFilled = this.keyCodeInput && this.keyCodeInput.value.length > 0;
    const numOk = this.keyNumberValid();
    const codeOk = this.keyCodeValid();

    if (this.keyNumberInput) this.keyNumberInput.classList.toggle('is-error', numFilled && !numOk);
    if (this.keyCodeInput) this.keyCodeInput.classList.toggle('is-error', codeFilled && !codeOk);
    if (this.keyNumberError) this.keyNumberError.hidden = !(numFilled && !numOk);
    if (this.keyCodeError) this.keyCodeError.hidden = !(codeFilled && !codeOk);
  }

  clearFieldErrors() {
    if (this.keyNumberInput) this.keyNumberInput.classList.remove('is-error');
    if (this.keyCodeInput) this.keyCodeInput.classList.remove('is-error');
    if (this.keyNumberError) this.keyNumberError.hidden = true;
    if (this.keyCodeError) this.keyCodeError.hidden = true;
  }

  // ---- rendering ---------------------------------------------------------
  render() {
    const valid = this.isValid();
    const value = this.getSelectedValue();

    // Panel visibility.
    if (this.panel) this.panel.hidden = !this.expanded;
    if (this.header) this.header.setAttribute('aria-expanded', String(this.expanded));

    // Collapsed + valid → show the selected option in the header + a summary
    // block; green border. Otherwise show the "Choose Key Option *" placeholder.
    const showSummary = !this.expanded && valid;

    if (this.placeholderEl) this.placeholderEl.hidden = showSummary;
    if (this.selectedTitleEl) {
      this.selectedTitleEl.hidden = !showSummary;
      if (showSummary) this.selectedTitleEl.textContent = this.labels[value] || value;
    }

    if (this.summaryEl) {
      if (showSummary && this.needsKeyFields(value)) {
        const num = this.keyNumberInput ? this.keyNumberInput.value.trim() : '';
        const code = this.keyCodeInput ? this.keyCodeInput.value.trim() : '';
        this.summaryEl.innerHTML = '';
        [
          `${this.keyNumberLabel} : ${num}`,
          `${this.keyCodeLabel} : ${code}`,
        ].forEach((line) => {
          const span = document.createElement('span');
          span.className = 'cart-drawer-key__summary-line';
          span.textContent = line;
          this.summaryEl.appendChild(span);
        });
        this.summaryEl.hidden = false;
      } else {
        this.summaryEl.hidden = true;
        this.summaryEl.innerHTML = '';
      }
    }

    if (this.box) this.box.classList.toggle('is-valid', showSummary);
  }

  // ---- cart note ---------------------------------------------------------
  buildNote() {
    const value = this.getSelectedValue();
    if (!value) return null;
    let note = `Key Option: ${value}`;
    if (this.needsKeyFields(value) && this.keyNumberValid() && this.keyCodeValid()) {
      note += `\nKey Number: ${this.keyNumberInput.value.trim()}\nKey Code: ${this.keyCodeInput.value.trim()}`;
    }
    return note;
  }

  /**
   * Persist the selection to the cart note. Only writes once the selection is
   * actually valid (so we never store a half-typed key). De-dupes identical
   * writes to avoid redundant network calls.
   */
  persist() {
    if (!this.isValid()) return Promise.resolve();
    const note = this.buildNote();
    if (note == null || note === this._lastNote) return Promise.resolve();
    this._lastNote = note;

    // Gate checkout while the write is in flight. Enabling the button the
    // instant a radio is checked would let a customer check out on a selection
    // that exists only in the browser — the order would carry no key option.
    this._notePending = true;
    this._noteFailed = false;
    this.updateCheckout();

    return fetch(`${(window.routes && window.routes.cart_update_url) || '/cart/update'}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ note }),
    })
      .then((r) => r.json())
      .catch((e) => {
        console.error('cart-drawer-key-options: note update failed', e);
        // Clear the de-dupe so the same selection can be written again, and
        // keep checkout gated — a required option that never reached the cart
        // must not be checkout-able.
        this._lastNote = null;
        this._noteFailed = true;
      })
      .finally(() => {
        this._notePending = false;
        this.updateCheckout();
      });
  }

  restoreFromNote() {
    const note = this.dataset.cartNote || '';
    if (!note) return;
    const match = note.match(/Key Option: (.*?)(?:\nKey Number: ([a-zA-Z0-9]{3,7})\nKey Code: (\d{7}))?$/);
    if (!match) return;

    const [, option, keyNumber, keyCode] = match;
    const radio = this.radios.find((r) => r.value === option);
    // Ignore a stored option whose card is now hidden (e.g. "Keyed Alike" when
    // the cart dropped to a single item) — force a fresh choice instead.
    if (!radio || this.isCardHidden(radio)) return;

    // The note survives an emptied cart, so it is only this cart's choice if the
    // customer made it here (see customerPickedThisPageSession). Otherwise leave the
    // radios blank and let the offer rules decide, which is what pre-selection does.
    //
    // "Use Existing Key Number" is the exception: the key number and code are typed
    // data that cannot be re-derived, so a complete one is always restored.
    const isTypedChoice = this.needsKeyFields(option) && !!keyNumber && !!keyCode;
    if (window.__ampKeyOptionPickedThisPageSession !== true && !isTypedChoice) return;

    radio.checked = true;
    this._lastNote = note;

    if (this.needsKeyFields(option) && keyNumber && keyCode) {
      if (this.keyNumberInput) this.keyNumberInput.value = keyNumber;
      if (this.keyCodeInput) this.keyCodeInput.value = keyCode;
    }

    this.cards.forEach((card) => {
      const r = card.querySelector('input[type="radio"]');
      card.classList.toggle('is-selected', !!r && r.checked);
    });
    if (this.fields) this.fields.hidden = !this.needsKeyFields(option);
  }

  // ---- checkout gating ---------------------------------------------------
  updateCheckout() {
    const button = document.getElementById('CartDrawer-Checkout');
    const actions = document.querySelector('.cart-drawer__actions');
    const selectionValid = this.isValid();
    // A selection only counts once the cart note write has landed.
    const noteSettled = !this._notePending && !this._noteFailed;
    const valid = selectionValid && noteSettled;

    if (actions) actions.classList.toggle('cart-drawer__actions--key-gated', !valid);

    if (!button) return;
    // Resolve the label element + its canonical text every time. The button
    // persists across quantity re-renders (it lives outside the re-rendered
    // region), so a fresh element instance must NOT capture the possibly-gated
    // live text — read the stable data-label instead.
    const label = button.querySelector('.cart-drawer-key-checkout-label');
    const originalText = (label && label.dataset.label) || (label && label.textContent) || '';

    if (valid) {
      button.disabled = false;
      button.classList.remove('cart-drawer__checkout--gated');
      if (label) label.textContent = originalText;
    } else {
      button.disabled = true;
      button.classList.add('cart-drawer__checkout--gated');
      // Only ask for a selection when one is genuinely missing — while a valid
      // choice is still being saved the label stays as it is.
      if (label) label.textContent = selectionValid ? originalText : this.disabledText;
    }
  }
}

if (!customElements.get('cart-drawer-key-options')) {
  customElements.define('cart-drawer-key-options', CartDrawerKeyOptions);
}
